<template>
  <div
    id="distributor-map"
    class="flex flex-col-reverse lg:flex-row gap-4"
  >
    <div
      class="bg-white rounded-lg shadow flex-1 p-3 border-2"
      :class="[
        {'border-primary': isAvailable === undefined},
        {'border-secondary': isAvailable === true},
        {'border-red-500': isAvailable === false}
      ]"
    >
      <div class="pt-2 pb-5 flex flex-row items-center justify-between gap-3">
        <p>
          🇫🇷 Disponible dans <span class="text-primary font-semibold">
            {{ availabilityCount }} {{ $filters.pluralize("agence", availabilityCount) }} sur {{ warehouses.length }}
          </span>
        </p>
        <base-spinner
          v-if="loading"
          size="small"
        />
        <icon
          v-else
          :name="isAvailable ? 'mdi-check-bold' : 'mdi-close-thick'"
          :color="isAvailable ? '#65efca' : 'red'"
          :size="25"
        />
      </div>
      <p v-if="loading" class="italic">Interrogation des grossistes en cours, cela peut prendre quelques instants</p>
      <Dialog v-model:open="modalOpen">
        <DialogTrigger class="w-full hover:opacity-75">
          <distributor-logistics-availability-map
            ref="mapRef"
            :loading="loading"
            :disabled="true"
            :open="infoWindowOpened"
            :data="data"
            :warehouses="warehouses"
            :info-content="infoContent"
            @toggle-info-window="toggleInfoWindow"
          />
        </DialogTrigger>
        <DialogContent class="min-w-[80%] max-h-auto p-10">
          <DialogTitle>
            <p>
              🇫🇷 Disponible dans <span class="text-primary font-semibold">
                {{ availabilityCount }} {{ $filters.pluralize("agence", availabilityCount) }} sur {{
                warehouses.length
              }}
              </span>
            </p>
          </DialogTitle>
          <distributor-logistics-availability-map
            ref="mapRef"
            :loading="loading"
            :open="infoWindowOpened"
            :data="data"
            :warehouses="warehouses"
            :info-content="infoContent"
            @toggle-info-window="toggleInfoWindow" />
        </DialogContent>
      </Dialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch} from "vue";
import {Warehouse} from '@/core/interfaces/warehouses';
import {
    DistributorProductAvailability,
    HealthcenterGroupDistributorDetails,
} from "@/disruption-logistics/interfaces/availability-providers";
import {Dialog, DialogTrigger, DialogTitle, DialogContent} from "@/core/components/ui/dialog";
import BaseSpinner from "@/core/components/base/spinner/BaseSpinner.vue";
import Icon from "@/core/components/icon/Icon.vue";
import DistributorLogisticsAvailabilityMap
    from "@/disruption-logistics/components/maps/distributors/DistributorLogisticsAvailabilityMap.vue";

const props = defineProps<{
    product: any,
    data?: HealthcenterGroupDistributorDetails[] | null,
    isAvailable?: boolean | null
}>();

const mapRef = ref<InstanceType<typeof DistributorLogisticsAvailabilityMap>>();
const infoWindowOpened = ref(false);
const modalOpen = ref(false);
const infoContent = ref<{
    warehouse: Warehouse,
    available: boolean | null,
    message: string | null,
    position: { lat: number, lng: number }
}>();

const loading = computed(() => {
    return !props.data;
});

/**
 * The warehouses.
 */
const warehouses = computed(() => {
    if (!props.data) {
        return [];
    }

    return props.data.map(datum => datum.warehouse);
});

/**
 * Get the data for the success availability.
 */
const successData = computed((): HealthcenterGroupDistributorDetails[] => {
    if (!props.data) {
        return [];
    }

    return props.data.filter(datum => datum.success);
});

/**
 * Get the count of the available warehouses.
 */
const availabilityCount = computed((): number => {
    return successData.value?.length ?? 0;
});

watch(() => warehouses.value, () => {
    if (!warehouses.value?.length) {
        return;
    }

    mapRef.value?.fitMapToWarehouses();
}, {deep: true});


watch(() => modalOpen.value, () => {
    if (modalOpen.value) {
        mapRef.value?.fitMapToWarehouses();
    }
});

/**
 * Get the availability for a warehouse.
 */
const warehouseAvailability = (warehouse: Warehouse): DistributorProductAvailability[] | undefined => {
    if (!props.data) {
        return undefined;
    }

    return props.data?.find(
        availability => availability.warehouse?.id === warehouse.id
    )?.products;
};

/**
 * Determine if the product is available in the warehouse.
 * It return null, if we doesn't have the availability for a warehouse
 *
 * @return {boolean|null}
 */
const isWarehouseAvailable = (warehouse: Warehouse): boolean | null => {
    const availabilities = warehouseAvailability(warehouse);

    if (!availabilities?.length) {
        return null;
    }

    return availabilities.some(product => product.isAvailable);
};

/**
 * Toggle warehouse info window.
 */
const toggleInfoWindow = (warehouse: Warehouse) => {
    const available = isWarehouseAvailable(warehouse);
    const availabilities = warehouseAvailability(warehouse);
    let message: string | null = null;

    if (availabilities?.length) {
        message = availabilities[0].message?.additif;
    }

    infoContent.value = {
        warehouse: warehouse,
        available,
        message,
        position: {
            lat: warehouse.geo_lat,
            lng: warehouse.geo_lon
        }
    };

    infoWindowOpened.value = true;
};
</script>
